/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import type { ComponentPropsWithoutRef, ComponentRef, Ref } from 'react';
import { Root as SwitchRoot, Thumb as SwitchThumb } from '@radix-ui/react-switch';

import { cn } from '@/lib/utils';

export type SwitchProps = ComponentPropsWithoutRef<typeof SwitchRoot> & {
    ref?: Ref<ComponentRef<typeof SwitchRoot>>;
    thumbClassName?: string;
};

function Switch({ className, ref, thumbClassName, ...props }: SwitchProps) {
    return (
        <SwitchRoot
            ref={ref}
            data-slot="switch"
            className={cn(
                'peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border border-border bg-border-subtle p-0.5 outline-none transition-[background-color,border-color,box-shadow,opacity] duration-200 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-[color:var(--focus)] disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-primary data-[state=checked]:bg-primary',
                className
            )}
            {...props}>
            <SwitchThumb
                data-slot="switch-thumb"
                className={cn(
                    'pointer-events-none block size-4 rounded-full bg-background shadow-xs transition-transform duration-200 data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0',
                    thumbClassName
                )}
            />
        </SwitchRoot>
    );
}

export { Switch };
